import {
    enableProdMode,
    inject,
    LOCALE_ID,
    provideAppInitializer,
    provideZoneChangeDetection, isDevMode,
} from "@angular/core";
import { provideRouter, withRouterConfig } from '@angular/router';

import { environment } from "./environment";
import { AppComponent } from "./app/app.component";
import { RC_PLATFORM_APP_PROVIDERS } from "./app/app.providers";
import { routes } from "./app/app.routes";
import { bootstrapApplication, Title } from "@angular/platform-browser";
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { KeycloakService } from "keycloak-angular";
import { installMediaFetchAuth, bridgeKeycloakToMediaCookie } from "./app/modules/authentication/interceptors/media-fetch-auth";
import { popperVariation, provideTippyConfig, tooltipVariation } from "@ngneat/helipopper";
import { provideServiceWorker } from '@angular/service-worker';
import { disableVerboseConsoleInProd } from './disable-console';
import { EnvironmentTitle } from './app/modules/shared/services/environment-title.service';
import { masquerAuDefilement } from './app/modules/shared/utils/tippy-masquer-au-defilement';
import { focusClavierSeulement } from './app/modules/shared/utils/tippy-focus-clavier';
import { NavigationHistoryService } from './app/modules/shared/services/navigation-history.service';

disableVerboseConsoleInProd();

registerLocaleData(localeFr);

if (typeof (Promise as any).try !== 'function') {
    (Promise as any).try = function promiseTry(callback: (...args: any[]) => any, ...args: any[]) {
        return new Promise(resolve => resolve(callback(...args)));
    };
}

const pdfWorkerSrc = '/assets/pdfjs-ng2/pdf.worker.min.mjs';
(window as any).pdfWorkerSrc = pdfWorkerSrc;

function initializeKeycloak(keycloak: KeycloakService) {
    return () => {
        const initPromise = keycloak.init({
            config: {
                url: environment.KEYCLOAK_URL,
                realm: environment.KEYCLOAK_REALM,
                clientId: environment.KEYCLOAK_CLIENT_ID
            },
            initOptions: {
                onLoad: 'check-sso',
                silentCheckSsoRedirectUri:
                    window.location.origin + '/assets/silent-check-sso.html',
                redirectUri: window.location.origin + window.location.pathname,
                checkLoginIframe: false, // Désactive le check iframe périodique pour accélérer le HMR
            },
            shouldAddToken: (request) => {
                const { method, url } = request;

                // Ne pas ajouter le token pour les fichiers statiques (GET /assets)
                const isGetRequest = 'GET' === method.toUpperCase();
                const excludedPaths = ['/assets', '/github', '/weather', 'netbox/api'];
                const isExcludedPath = excludedPaths.some((path) => url.includes(path));

                // Exclure aussi les URLs externes (RC-STOCKAGE, etc.)
                const externalPatterns = ['http://127.0.0.1:8001'];
                const isExternalUrl = externalPatterns.some((pattern) => url.includes(pattern));

                return !(isGetRequest && isExcludedPath) && !isExternalUrl;
            },
        });
        // Non bloquant : ne retarde pas le démarrage de l'app sur cet appel.
        initPromise.then(() => bridgeKeycloakToMediaCookie(keycloak));
        return initPromise;
    };
}

if (environment.production) {
    enableProdMode();
}

function bootstrap() {
    bootstrapApplication(AppComponent, {
        providers: [
            // provideZonelessChangeDetection(),
            // Regrouper aussi les multiples NgZone.run() d'une même frame : les flux
            // websocket, overlays et panneaux ne doivent pas provoquer plusieurs
            // rendus complets successifs pour une seule interaction utilisateur.
            provideZoneChangeDetection({ eventCoalescing: true, runCoalescing: true }),
            provideTippyConfig({
                defaultVariation: 'tooltip',
                variations: {
                    tooltip: {
                        ...tooltipVariation,
                        // 'focus' en plus du survol : les infobulles s'affichent
                        // aussi en navigation clavier (la variation par défaut
                        // d'helipopper est mouseenter seul)
                        trigger: 'mouseenter focus',
                        // Pas d'infobulle au toucher : sans `mouseleave`, celle déclenchée
                        // par le tap qui ouvre un panneau (ligne de DIT…) restait affichée
                        // par-dessus. Tippy la réactive dès qu'une souris bouge (écran hybride).
                        // Les poppers au clic (variation `popper`) ne sont pas concernés.
                        touch: false,
                        // Une infobulle disparaît au défilement au lieu de suivre son élément.
                        plugins: [masquerAuDefilement, focusClavierSeulement],
                    },
                    popper: popperVariation,
                }
            }),
            // View Transitions API désactivée : provoquait des gels de navigation
            // intermittents (overlay ::view-transition figé, reload obligatoire) que les
            // skipTransition() cas-par-cas ne couvraient pas entièrement.
            provideRouter(routes, withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' })),
            ...RC_PLATFORM_APP_PROVIDERS,
            { provide: LOCALE_ID, useValue: 'fr' },
            // Marque l'onglet hors production (LOCAL / DEV), y compris quand le titre
            // est réécrit par la TitleStrategy du Router ou par les notifications.
            { provide: Title, useClass: EnvironmentTitle },
            provideAppInitializer(() => {
                const initializerFn = (initializeKeycloak)(inject(KeycloakService));
                return initializerFn();
            }),
            provideAppInitializer(() => {
                installMediaFetchAuth(inject(KeycloakService));
            }),
            // Doit observer le Router dès la première navigation : créé à la demande, il
            // naîtrait en même temps que la page portant le bouton « retour » et la
            // croirait toujours première de la session (repli sur la route par défaut).
            provideAppInitializer(() => {
                inject(NavigationHistoryService);
            }),
            KeycloakService,
            provideServiceWorker('ngsw-worker.js', {
                enabled: !isDevMode(),
                registrationStrategy: 'registerWhenStable:30000'
            })
        ]
    })
        .catch((err) => console.error(err));
}


if (document.readyState === 'complete') {
    bootstrap();
} else {
    document.addEventListener('DOMContentLoaded', bootstrap);
}

